import { toaster } from '@/core/ui';
import {
  AdminQuestionDocument,
  CreateQuestionRecordReviewDocument,
  DeleteQuestionRecordReviewByRecordIdsDocument,
  ManageQuestionCommentDocument,
  ResetQuestionDocument,
  type ResetQuestionMutationVariables,
  UpdateQuestionDocument,
} from '@/graphql/gql/graphql';
import { useMutation } from '@/libs/apollo/export';
import { useQuestionState } from '@/store/question';
import { getChangedFields } from '@/utils/object';

type ISchema = {
  question: string;
  choices: {
    name: string;
    answer: boolean;
    label: string;
    votes?: number | null | undefined;
    explanation?: string | null | undefined;
  }[];
  enabled: boolean;
  createRevision: boolean;
  explanation?: string | null | undefined;
  learningPoint?: string | null | undefined;
};

export const useQuestion = () => {
  const { question, setUpdateLoading } = useQuestionState();
  const [resetQuestion, { loading }] = useMutation(ResetQuestionDocument, {
    refetchQueries: [AdminQuestionDocument],
  });
  const [manageQuestionComment, { loading: manageQuestionCommentLoading }] = useMutation(
    ManageQuestionCommentDocument,
    { refetchQueries: [AdminQuestionDocument] },
  );
  const [createQuestionRecordReview, { loading: createQuestionRecordReviewLoading }] = useMutation(
    CreateQuestionRecordReviewDocument,
    { refetchQueries: [AdminQuestionDocument] },
  );
  const [deleteQuestionRecordReview, { loading: deleteQuestionRecordReviewLoading }] = useMutation(
    DeleteQuestionRecordReviewByRecordIdsDocument,
    { refetchQueries: [AdminQuestionDocument] },
  );
  const [updateQuestion] = useMutation(UpdateQuestionDocument, {
    refetchQueries: [AdminQuestionDocument, 'QuestionData'],
  });

  const handleResetQuestion = (operation: ResetQuestionMutationVariables['operation']) => {
    if (!question) return;

    const idsMap: Record<ResetQuestionMutationVariables['operation'], number[]> = {
      likes: [question.id],
      dislikes: [question.id],
      votes: question.choices.map((c) => c.id),
    };

    resetQuestion({ variables: { ids: idsMap[operation] as unknown as string[], operation } })
      .then((res) => {
        if (res.data?.admin?.resetQuestion) {
          toaster.success(`${operation} completed successfully`);
        } else {
          toaster.error(`${operation} failed`);
        }
      })
      .catch((err) => {
        toaster.error(err.message || `${operation} failed`);
      });
  };

  const handleResetVote = (label: string) => {
    const choiceId = question?.choices?.find((c) => c.label === label)?.id;
    if (!choiceId) return;
    resetQuestion({ variables: { ids: [choiceId as unknown as string], operation: 'votes' } })
      .then((res) => {
        if (res.data?.admin?.resetQuestion) {
          toaster.success(`Votes reset successfully for choice label: ${label}`);
        } else {
          toaster.error('Failed to reset votes');
        }
      })
      .catch((err) => {
        toaster.error(err.message || 'Failed to reset votes');
      });
  };

  const handleCreateQuestionRecordReview = (questionId: number) => {
    if (!question) return;
    createQuestionRecordReview({ variables: { input: [{ recordId: Number(questionId), type: 1 }] } }).then(
      (res) => {
        if (res.data?.restricted?.createRecordReview) {
          toaster.success(`Question added to review successfully`);
        } else {
          toaster.error(`Failed to add question to review`);
        }
      },
    );
  };

  const handleDeleteRecordReview = (questionId: number) => {
    if (!question) return;
    deleteQuestionRecordReview({ variables: { recordIds: [questionId.toString()] } }).then((res) => {
      if (res.data?.restricted?.deleteRecordReviewByRecordIds) {
        toaster.success(`Question removed from review successfully`);
      } else {
        toaster.error(`Failed to remove question from review`);
      }
    });
  };

  const handleManageQuestionComment = (commentId: number, operation: string) => {
    if (!question) return;
    manageQuestionComment({ variables: { ids: [commentId.toString()], operation } })
      .then((res) => {
        if (res.data?.admin?.manageQuestionComment) {
          toaster.success(`Comment ${operation} done`);
        } else {
          toaster.error(`Comment ${operation} failed`);
        }
      })
      .catch((err) => {
        toaster.error(err.message || `Comment ${operation} failed`);
      });
  };

  const handleSaveQuestion = (userInput: ISchema, dirtyFields: Partial<Record<keyof ISchema, true>>) => {
    setUpdateLoading(true);
    try {
      if (!question) return;
      const { id: questionId } = question;

      if (!questionId) {
        toaster.error('Question ID is required');
        return;
      }

      /** TODO:: Replace with fields value from dirtyFields */
      const changedFields = getChangedFields<ISchema>(
        userInput,
        {
          ...(question || {}),
          enabled: question?.enabled ?? false,
          createRevision: userInput.createRevision ?? false,
        } as any,
        dirtyFields,
      );

      if (Object.keys(changedFields).length === 0) {
        toaster.info('No changes are different from the original question');
        return;
      }

      const updates = { ...changedFields, id: questionId } as any;
      if (changedFields.choices) {
        const answerChoice = changedFields.choices.find((choice) => choice.answer);
        updates.answer = JSON.stringify(answerChoice ? [answerChoice.label] : ['a']);
        updates.choices = changedFields.choices.map((c) => {
          const { votes, ...choiceWithoutVotes } = c;
          return { ...choiceWithoutVotes, questionId };
        });
      }

      delete updates.enabled;

      if (Object.hasOwn(changedFields, 'enabled')) {
        updates.enabled = Number(changedFields.enabled);
      }
      updateQuestion({ variables: { input: updates } })
        .then((res) => {
          if (res.data?.admin?.updateQuestion) {
            toaster.success('Question updated successfully');
          } else {
            toaster.error('Failed to update question');
          }
        })
        .catch((err) => {
          toaster.error(err.message || 'Failed to update question');
        });
    } finally {
      setUpdateLoading(false);
    }
  };

  return {
    handleResetQuestion,
    handleResetVote,
    handleManageQuestionComment,
    loading,
    handleSaveQuestion,
    manageQuestionCommentLoading,
    handleCreateQuestionRecordReview,
    handleDeleteRecordReview,
    createQuestionRecordReviewLoading,
    deleteQuestionRecordReviewLoading,
  };
};
